import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Card, Col, Form, Radio, Row } from "antd";
import { getMinMaxValidationRules, getValidationProps } from "../../libs/form";
import { InputField, ReactSelectField, TextAreaField } from "../../atoms/FormElement";
import AddOwnerBankDetail from "./AddOwnerBankDetail";
import { getConfigData } from "../Properties/ducks/actions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { useDropdownOptions } from "../../../hooks/useFormattedSelectOptions";
import { useCreateOwner } from "./api/useCreateOwner";
import { useApiWithFilters } from "../../../hooks/search/useApiWithFilters";
import { PointContact } from "./PointContact";
import { managementService } from "../../../configs/constants";
import { useMasterUserOptions } from "../../../hooks/useMasterUserOptions";
import OwnerUserLinking from "./OwnerUserLinking";
interface AddOwnerProps {
  setVisible?: (visible: boolean) => void; // optional function
  setOwnerFetch?: (visible: boolean) => void; // optional function
  editAbleRecord?: any; // optional object (you can type this better later)
  ownerInitial?: any; // optional value
  ownerFetch?: boolean;
  setIsSubmit?: (boolean) => void;
  isSubmit?: boolean;
}

const AddOwner: React.FC<AddOwnerProps> = ({
  setVisible,
  editAbleRecord,
  ownerInitial,
  setOwnerFetch,
  ownerFetch,
  setIsSubmit,
  isSubmit,
}) => {
  const { options } = useMasterUserOptions(managementService);
  const assignUser = options?.find((option) => option.value === editAbleRecord?.userId);
  const [ownerType, setOwnerType] = useState<"company" | "individual">(editAbleRecord?.identityType || "company");
  const dispatch = useDispatch<AppDispatch>();
  const configData = useSelector((state: any) => state.dashboard.configData);
  const nationalityOptions = useDropdownOptions(configData?.nationalityList);
  const [assignedUser, setAssignedUser] = useState<any>(null);
  const { loading, error, createOwner } = useCreateOwner();

  useEffect(() => {
    dispatch(getConfigData(["bankList", "nationalityList"]));
  }, []);

  const {
    data: result,
    loading: isLoading,
    error: getError,
    fetchData,
  } = useApiWithFilters({
    apiUrl: `/property/beneficiary?ownerId=${editAbleRecord?.ownerId}`,
    method: "GET",
  });

  useEffect(() => {
    editAbleRecord?.ownerId && fetchData(); // initial load
    let country =
      editAbleRecord?.nationality &&
      nationalityOptions?.find((nationality) => nationality?.label === editAbleRecord?.nationality);
    setValue("nationality", country);
  }, []);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    resetField,
    clearErrors,
    formState: { errors },
  } = useForm({});

  const submitData = async (data: any) => {
    try {
      let payLoad = data;
      payLoad["identityType"] = ownerType;
      payLoad["userId"] = assignedUser?.value;
      const statusCode = await createOwner(payLoad, editAbleRecord?.ownerId);
      setOwnerFetch && setOwnerFetch(!ownerFetch);
      if (statusCode) setVisible(false);
      reset();
      setIsSubmit(!isSubmit);
    } catch (error) {
      console.log(error);
    }
  };

  const onOwnerTypeChange = (value) => {
    setOwnerType(value);
    !editAbleRecord?.ownerId && reset();
  };
  const defaultProps = { control };
  useEffect(() => {
    if (editAbleRecord?.nationality) return;
    setValue("nationality", null, { shouldValidate: false });
    clearErrors("nationality");
  }, [ownerType]);

  useEffect(() => {
    assignUser && setAssignedUser(assignUser);
  }, [assignUser]);

  return (
    <Form layout="vertical" className="bf-main" onFinish={handleSubmit(submitData)}>
      {/* ---------------- Owner Detail Section ---------------- */}
      <Card
        loading={loading}
        title="Owner Detail"
        style={{ marginBottom: 10 }}
        extra={
          options?.length > 0 && (
            <OwnerUserLinking
              setAssignedUser={setAssignedUser}
              assignedUser={assignedUser && assignedUser}
              control={control}
              setValue={setValue}
              userOptions={options}
            />
          )
        }
      >
        <Col span={24}>
          <Row>
            <Form.Item label="Owner Type" name="identityType">
              <Radio.Group
                optionType="default"
                defaultValue={ownerType}
                onChange={(e) => onOwnerTypeChange(e.target.value)}
                options={[
                  { label: "Company", value: "company" },
                  { label: "Individual", value: "individual" },
                ]}
              />
            </Form.Item>
          </Row>

          <Row gutter={24}>
            <Col span={8}>
              <InputField
                fieldname="ownerName"
                label={"Owner Name"}
                {...defaultProps}
                {...getValidationProps({
                  fieldName: "ownerName",
                  errors,
                  isRequired: true,
                })}
                iProps={{
                  placeholder: `Enter owner name`,
                }}
                setValue={setValue}
                valueGot={editAbleRecord?.name || ownerInitial?.value}
              />
            </Col>
            {
              <Col span={8}>
                <InputField
                  fieldname="ownerNo"
                  label="Owner No."
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: "ownerNo",
                    errors,
                    isRequired: true,
                  })}
                  iProps={{ placeholder: "Enter owner number" }}
                  setValue={setValue}
                  valueGot={editAbleRecord?.number || ""}
                />
              </Col>
            }
            {ownerType === "company" && (
              <Col span={8}>
                <InputField
                  fieldname="companyName"
                  label="Company Name."
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: "companyName",
                    errors,
                    isRequired: true,
                  })}
                  iProps={{ placeholder: "Enter Company Name" }}
                  setValue={setValue}
                  valueGot={editAbleRecord?.companyName || ""}
                />
              </Col>
            )}

            <Col span={8}>
              <InputField
                fieldname="ownerEmail"
                label="Email Address"
                {...defaultProps}
                {...getValidationProps({
                  fieldName: "ownerEmail",
                  errors,
                  isRequired: true,
                })}
                iProps={{ placeholder: "Enter email address" }}
                setValue={setValue}
                valueGot={editAbleRecord?.email || ""}
              />
            </Col>

            <Col span={8}>
              <InputField
                fieldname="ownerPhone"
                label="Mobile Phone"
                {...defaultProps}
                {...getValidationProps({
                  fieldName: "ownerPhone",
                  errors,
                  isRequired: true,
                  rules: getMinMaxValidationRules({
                    label: "Mobile Phone",
                    min: 9,
                    max: 9,
                  }),
                })}
                iProps={{ addonBefore: "+971", placeholder: "123456789" }}
                setValue={setValue}
                valueGot={editAbleRecord?.phone || ""}
              />
            </Col>
            <Col span={8}>
              <InputField
                fieldname="workPhone"
                label="Work Phone"
                {...defaultProps}
                {...getValidationProps({
                  fieldName: "workPhone",
                  errors,
                  //isRequired: true,
                  rules: getMinMaxValidationRules({
                    label: "Work Phone",
                    // min: 9,
                    //  max: 9,
                  }),
                })}
                iProps={{ addonBefore: "+971", placeholder: "123456789" }}
                setValue={setValue}
                valueGot={editAbleRecord?.workPhone || ""}
              />
            </Col>
            <Col span={8} key={ownerType}>
              <ReactSelectField
                fieldname="nationality"
                label="Nationality"
                selectOption={nationalityOptions}
                {...defaultProps}
                {...getValidationProps({
                  fieldName: "nationality",
                  errors,
                  isRequired: true,
                })}
                iProps={{ placeholder: "Select nationality" }}
                setValue={setValue}
              />
            </Col>
            <Col span={8}>
              <InputField
                fieldname="ownerCity"
                label="City"
                {...defaultProps}
                {...getValidationProps({
                  fieldName: "ownerCity",
                  errors,
                  isRequired: true,
                })}
                iProps={{ placeholder: "Enter city" }}
                setValue={setValue}
                valueGot={editAbleRecord?.city || ""}
              />
            </Col>

            {ownerType !== "company" && (
              <Col span={8}>
                <InputField
                  fieldname="address"
                  label="Address."
                  {...defaultProps}
                  {...getValidationProps({
                    fieldName: "address",
                    errors,
                    isRequired: true,
                  })}
                  iProps={{ placeholder: "Enter address" }}
                  setValue={setValue}
                  valueGot={editAbleRecord?.address || ""}
                />
              </Col>
            )}

            {ownerType === "company" && (
              <>
                <Col span={8}>
                  <InputField
                    fieldname="officeAddress"
                    label="Office Address"
                    {...defaultProps}
                    {...getValidationProps({
                      fieldName: "officeAddress",
                      errors,
                      isRequired: true,
                    })}
                    iProps={{ placeholder: "Enter office address" }}
                    setValue={setValue}
                    valueGot={editAbleRecord?.address || ""}
                  />
                </Col>

                <Col span={8}>
                  <InputField
                    fieldname="poBox"
                    label="PO Box"
                    {...defaultProps}
                    {...getValidationProps({
                      fieldName: "poBox",
                      errors,
                      isRequired: true,
                    })}
                    iProps={{ placeholder: "Enter PO Box" }}
                    setValue={setValue}
                    valueGot={editAbleRecord?.poBox || ""}
                  />
                </Col>

                <Col span={8}>
                  <InputField
                    fieldname="licenseNo"
                    label="License No."
                    {...defaultProps}
                    {...getValidationProps({
                      fieldName: "licenseNo",
                      errors,
                      isRequired: true,
                    })}
                    iProps={{ placeholder: "Enter license number" }}
                    setValue={setValue}
                    valueGot={editAbleRecord?.tradeLicenseNumber || ""}
                  />
                </Col>
              </>
            )}
            <Col span={24}>
              <TextAreaField
                label={`Note`}
                fieldname="note"
                iProps={{ placeholder: "Note...." }}
                setValue={setValue}
                control={control}
                valueGot={editAbleRecord?.note || ""}
              />
            </Col>
          </Row>
        </Col>
      </Card>

      {/* ---------------- Point of Contact Section ---------------- */}
      <PointContact
        setValue={setValue}
        control={control}
        errors={errors}
        configData={configData}
        getValidationProps={getValidationProps}
        loading={loading}
        editAbleRecord={editAbleRecord}
        result={result}
      />
      {/* ---------------- Bank Detail Section ---------------- */}
      <AddOwnerBankDetail
        control={control}
        errors={errors}
        configData={configData}
        getValidationProps={getValidationProps}
        loading={loading}
        ownerId={editAbleRecord?.ownerId}
        result={result}
      />

      <Col span={24}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "10px",
          }}
        >
          <Button onClick={() => setVisible(false)} type="default" size="large" className="csv_publish">
            Back
          </Button>
          <Button disabled={loading} htmlType="submit" type="primary" size="large" className="csv_publish">
            {loading ? "Loading..." : editAbleRecord ? "Update" : "Submit"}
          </Button>
        </div>
      </Col>
    </Form>
  );
};

export default AddOwner;
